import { GetRowIdParams, GridReadyEvent, RowSelectedEvent } from "@ag-grid-community/core";
import { Component, OnInit, ViewEncapsulation } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { AgFns, GridOptionsExt } from "app/shared/ag-fns";
import { DialogBaseComponent } from "app/shared/dialog-base.component";
import { takeUntil } from "rxjs/operators";
import { ReturnPhase, ReturnPhaseLabel } from "../model/enums/return-phase";
import { ReturnRequestDetail } from "../model/entities/entity-model";
import { DomainBaseComponent } from "../shared/domain-base.component";
import { DomainService } from "../shared/domain.service";
import { ReceiveRrdDialogComponent } from "./receive-rrd-dialog.component";
import { InspectRrdDialogComponent } from "./inspect-rrd-dialog.component";
import { RouteRrdDialogComponent } from "./route-rrd-dialog.component";
import { SettleRrdDialogComponent } from "./settle-rrd-dialog.component";
import { ReconcileRrdDialogComponent } from "./reconcile-rrd-dialog.component";
import { RecoverRrdDialogComponent } from "./recover-rrd-dialog.component";

/**
 * v2 Returns Inbox — unified worklist.
 *
 * Lists every RRD currently in any active phase (everything except CLOSED).
 * The Phase column shows the current state and is filterable, so the operator
 * sees the whole pipeline at once and can find a specific RRD wherever it
 * happens to be. The action button label adapts per-row to the current phase.
 *
 * Pass 1 only implements the RECEIVING-phase action. Clicking the action
 * button on a non-RECEIVING row will open the Workspace, which currently
 * shows a "phase not yet implemented" message for those rows.
 */
@Component({
  selector: "app-returns-inbox",
  templateUrl: "./returns-inbox.component.html",
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class ReturnsInboxComponent extends DomainBaseComponent implements OnInit {
  gridOptions: GridOptionsExt;
  rrds: ReturnRequestDetail[] = [];

  constructor(
    domainService: DomainService,
    private route: ActivatedRoute
  ) {
    super(domainService);
    this.route.queryParamMap.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.updateFromContext();
    });
  }

  async updateFromContext() {
    this.gridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onGridReady.bind(this),
      onRowSelected: this.onRowSelected.bind(this),
      getRowId: (params: GetRowIdParams) => params.data?.id?.toString() ?? "",
    });
    this.isPageReady = true;
  }

  async onGridReady(event: GridReadyEvent) {
    const colDefs = [
      { headerName: "RRD #", field: "id", filter: "agNumberColumnFilter", type: "leftAligned", ...AgFns.fixWidth(90) },
      {
        ...AgFns.createButtonProps("Action", this.openAction.bind(this), {
          calcLabel: (rrd: ReturnRequestDetail) => this.getActionLabel(rrd?.phase),
        } as any),
        ...AgFns.fixWidth(140),
      },
      {
        ...AgFns.createButtonProps("Recover", this.openRecovery.bind(this), {
          label: "Recover",
          calcDisabled: (rrd: ReturnRequestDetail) => rrd?.phase === ReturnPhase.Receiving,
          tooltip: "Recover is available once the RRD is past Receiving.",
        } as any),
        ...AgFns.fixWidth(120),
      },
      {
        headerName: "Phase",
        field: "phase",
        filter: "agSetColumnFilter",
        valueFormatter: (p: any) => ReturnPhaseLabel[p.value as ReturnPhase] || p.value || "",
        ...AgFns.fixWidth(140),
      },
      {
        headerName: "Job Order",
        field: "returnRequest.joHeader.id",
        filter: "agNumberColumnFilter",
        type: "leftAligned",
        ...AgFns.fixWidth(120),
      },
      {
        headerName: "Account",
        field: "returnRequest.joHeader.account.accountName",
        filter: "agTextColumnFilter",
        ...AgFns.flexWidth(200),
      },
      {
        headerName: "Submitted Date",
        field: "returnRequest.receivedTs",
        filter: "agDateColumnFilter",
        ...AgFns.fixWidth("date"),
      },
      {
        headerName: "Product",
        field: "joDetail.productTypeConfigName",
        filter: "agTextColumnFilter",
        ...AgFns.flexWidth(240),
      },
      { headerName: "Declared Qty", field: "qty", filter: "agNumberColumnFilter", type: "leftAligned", ...AgFns.fixWidth(120) },
      { headerName: "Received Qty", field: "receivedQty", filter: "agNumberColumnFilter", type: "leftAligned", ...AgFns.fixWidth(120) },
      {
        headerName: "Reason",
        field: "returnReasonType.name",
        filter: "agTextColumnFilter",
        ...AgFns.fixWidth(160),
      },
      {
        headerName: "Customer Notes",
        field: "returnExplanation",
        filter: "agTextColumnFilter",
        ...AgFns.flexWidth(240),
      },
    ];
    AgFns.initGrid(event, colDefs, [
      { colId: "phase", sort: "asc" },
      { colId: "id", sort: "asc" },
    ]);
    await this.loadActive();
  }

  async loadActive() {
    this.rrds = await this.dbQueryService.getActiveReturnRequestDetails();
    this.gridOptions.apiX?.setGridOption("rowData", this.rrds);
  }

  /** Per-row action label driven by current phase. */
  private getActionLabel(phase: string | null | undefined): string {
    switch (phase) {
      case ReturnPhase.Receiving:    return "Receive";
      case ReturnPhase.Inspecting:   return "Inspect";
      case ReturnPhase.Routing:      return "Route";
      case ReturnPhase.Settling:     return "Settle";
      case ReturnPhase.Unreconciled: return "Reconcile";
      default:                       return "Open";
    }
  }

  onRowSelected(_e: RowSelectedEvent) {}

  /**
   * Dispatch to the phase-specific dialog. Pass 1 only implements the
   * RECEIVING dialog. Later passes add Inspect/Route/Settle/Reconcile.
   * On a successful close, the affected RRD is refreshed in place.
   */
  async openAction(rrd: ReturnRequestDetail) {
    if (!rrd) return;
    let result: any = null;
    switch (rrd.phase) {
      case ReturnPhase.Receiving:
        result = await DialogBaseComponent.open(ReceiveRrdDialogComponent, this, {
          rrdId: rrd.id,
          header: `Receive — RRD #${rrd.id}`,
          dialogWidth: "600px",
          dialogHeight: "auto",
        } as any);
        break;
      case ReturnPhase.Inspecting:
        result = await DialogBaseComponent.open(InspectRrdDialogComponent, this, {
          rrdId: rrd.id,
          header: `Inspect — RRD #${rrd.id}`,
          dialogWidth: "650px",
          dialogHeight: "auto",
        } as any);
        break;
      case ReturnPhase.Routing:
        result = await DialogBaseComponent.open(RouteRrdDialogComponent, this, {
          rrdId: rrd.id,
          header: `Route — RRD #${rrd.id}`,
          dialogWidth: "600px",
          dialogHeight: "auto",
        } as any);
        break;
      case ReturnPhase.Settling:
        result = await DialogBaseComponent.open(SettleRrdDialogComponent, this, {
          rrdId: rrd.id,
          header: `Settle — RRD #${rrd.id}`,
          dialogWidth: "600px",
          dialogHeight: "auto",
        } as any);
        break;
      case ReturnPhase.Unreconciled:
        result = await DialogBaseComponent.open(ReconcileRrdDialogComponent, this, {
          rrdId: rrd.id,
          header: `Reconcile — RRD #${rrd.id}`,
          dialogWidth: "600px",
          dialogHeight: "auto",
        } as any);
        break;
      default:
        return;
    }
    if (result === true) {
      await this.refreshRrdRow(rrd.id);
    }
  }

  /** Open the Recovery dialog (any active phase → RECEIVING reset). */
  async openRecovery(rrd: ReturnRequestDetail) {
    if (!rrd) return;
    const result = await DialogBaseComponent.open(RecoverRrdDialogComponent, this, {
      rrdId: rrd.id,
      header: `Recover — RRD #${rrd.id}`,
      dialogWidth: "600px",
      dialogHeight: "auto",
    } as any);
    if (result === true) {
      await this.refreshRrdRow(rrd.id);
    }
  }

  /** Re-fetch one RRD and update the grid row in place. */
  private async refreshRrdRow(rrdId: number) {
    const fresh = await this.uow.fetchEntityByKey(ReturnRequestDetail, rrdId);
    const entity = fresh?.entity as ReturnRequestDetail | null;
    if (!entity) return;
    // If the RRD is now CLOSED, drop it from the active list and rebind rowData;
    // otherwise the entity is the same Breeze instance (already updated in place
    // by the merge), so just tell the grid to redraw that row.
    if (entity.phase === ReturnPhase.Closed) {
      this.rrds = this.rrds.filter(r => r.id !== rrdId);
      this.gridOptions.apiX?.setGridOption("rowData", this.rrds);
    } else {
      const idx = this.rrds.findIndex(r => r.id === rrdId);
      if (idx >= 0) this.rrds[idx] = entity;
      const rowNode = this.gridOptions.apiX?.getRowNode(String(rrdId));
      if (rowNode) {
        rowNode.setData(entity);
        this.gridOptions.apiX?.redrawRows({ rowNodes: [rowNode] });
      } else {
        this.gridOptions.apiX?.setGridOption("rowData", this.rrds);
      }
    }
  }
}
